import { Sentry, setContext } from '../config/sentry';

// Interface for Statsig context
interface _StatsigContext {
  experiments?: Record<string, string>;
  featureGates?: Record<string, boolean>;
  dynamicConfigs?: Record<string, unknown>;
  userId?: string;
}

// Interface for window.statsig global
interface StatsigClient {
  getCurrentUser?: () => { userID?: string; customIDs?: Record<string, string> };
  getAllExperiments?: () => Array<{ name: string; group: string }>;
  getAllGates?: () => Array<{ name: string; value: boolean }>;
  getAllConfigs?: () => Array<{ name: string; value: unknown }>;
  getSDKVersion?: () => string;
  logEvent?: (eventName: string, value?: number, metadata?: Record<string, unknown>) => void;
}

// Initialize Statsig integration with Sentry
export function initStatsigSentryIntegration() {
  // Add event processor to include Statsig context in all errors
  Sentry.addEventProcessor((event) => {
    try {
      // Get Statsig instance if available
      const statsig = (window as Window & { statsig?: StatsigClient }).statsig;

      if (statsig) {
        // Get current user and feature flag states
        const user = statsig.getCurrentUser?.();
        const experiments = statsig.getAllExperiments?.() || [];
        const gates = statsig.getAllGates?.() || [];
        const configs = statsig.getAllConfigs?.() || [];

        // Add Statsig context to the event
        event.contexts = {
          ...event.contexts,
          statsig: {
            user_id: user?.userID,
            custom_ids: user?.customIDs,
            experiments: Object.fromEntries(
              experiments.map((exp) => [exp.name, exp.group])
            ),
            feature_gates: Object.fromEntries(
              gates.map((gate) => [gate.name, gate.value])
            ),
            dynamic_configs: Object.fromEntries(
              configs.map((config) => [config.name, config.value])
            ),
          },
        };

        // Add tags for key feature flags
        gates.forEach((gate) => {
          if (gate.value) {
            event.tags = {
              ...event.tags,
              [`statsig.gate.${gate.name}`]: 'true',
            };
          }
        });

        // Add experiment groups as tags
        experiments.forEach((exp) => {
          event.tags = {
            ...event.tags,
            [`statsig.exp.${exp.name}`]: exp.group,
          };
        });
      }
    } catch (error) {
      // Don't let Statsig integration errors break error reporting
      console.warn('Failed to add Statsig context to Sentry:', error);
    }

    return event;
  });
}

// Track Statsig-specific errors
export function trackStatsigError(error: Error, operation: string, metadata?: Record<string, unknown>) {
  Sentry.withScope((scope) => {
    scope.setTag('statsig.operation', operation);
    scope.setContext('statsigerror', {
      operation,
      metadata,
      sdk_version: (window as Window & { statsig?: StatsigClient }).statsig?.getSDKVersion?.(),
    });

    Sentry.captureException(error);
  });
}

// Helper to capture feature flag evaluation errors
export function trackFeatureFlagError(
  flagName: string,
  error: Error,
  defaultValue: unknown
) {
  Sentry.withScope((scope) => {
    scope.setTag('statsig.flagerror', flagName);
    scope.setContext('feature_flagerror', {
      flag_name: flagName,
      default_value: defaultValue,
      error_message: error.message,
    });

    scope.setLevel('warning');
    Sentry.captureException(error);
  });
}

// Helper to track experiment exposure
export function trackExperimentExposure(
  experimentName: string,
  group: string,
  userId?: string
) {
  Sentry.addBreadcrumb({
    category: 'statsig.experiment',
    message: `User exposed to experiment: ${experimentName}`,
    level: 'info',
    data: {
      experiment: experimentName,
      group,
      user_id: userId,
    },
  });
}

// Update Statsig context in Sentry when user changes
export function updateStatsigUserContext(userId: string | null) {
  if (userId) {
    setContext('statsig_user', {
      user_id: userId,
      last_updated: new Date().toISOString(),
    });
  }
}

// Track A/B test conversions with error context
export function trackConversionWithContext(
  eventName: string,
  value?: number,
  metadata?: Record<string, unknown>
) {
  try {
    const statsig = (window as Window & { statsig?: StatsigClient }).statsig;
    if (statsig?.logEvent) {
      statsig.logEvent(eventName, value, metadata);

      // Add breadcrumb for successful conversion
      Sentry.addBreadcrumb({
        category: 'statsig.conversion',
        message: `Conversion tracked: ${eventName}`,
        level: 'info',
        data: {
          event: eventName,
          value,
          metadata,
        },
      });
    }
  } catch (error) {
    trackStatsigError(error as Error, 'logEvent', {
      event: eventName,
      value,
      metadata,
    });
  }
}